// Copyright (c) Ping. All rights reserved.

import React, { useCallback, useMemo } from 'react';
import {
  Pressable,
  StyleSheet,
  Text,
  type TextStyle,
  View,
  type ViewStyle,
  useColorScheme,
} from 'react-native';
import { Avatar } from '../components/Avatar';
import { createTheme } from '../design-system/theme';
import { formatReplyCount, threadsStrings } from './strings';
import { visibleParticipants } from './threadSummary';
import type { ThreadSummary } from './types';

export interface ThreadInChatProps {
  readonly summary: ThreadSummary;
  /** Hover state matches the Figma `state=hover` variant. */
  readonly hovered?: boolean;
  readonly onOpen: (summary: ThreadSummary) => void;
  readonly onShowOptions?: ((summary: ThreadSummary) => void) | undefined;
  readonly testID?: string;
}

const LEAD_AVATAR_SIZE = 20;
const ICON_SIZE = 16;

/**
 * Inline affordance shown under a parent message in the main chat feed.
 * Mirrors the Figma `Thread` component set (`2938:10511`). The host owns
 * deciding whether to render this (typically when `summary.replyCount >
 * 0`); rendering with `replyCount === 0` shows the "No replies" copy.
 */
export const ThreadInChat = React.memo<ThreadInChatProps>(
  ({ summary, hovered = false, onOpen, onShowOptions, testID }) => {
    const scheme = useColorScheme();
    const colorScheme: 'light' | 'dark' = scheme === 'dark' ? 'dark' : 'light';
    const theme = createTheme(colorScheme);

    const lead = useMemo(() => {
      const { visible } = visibleParticipants(summary.recentParticipants, 1);
      return visible[0] ?? null;
    }, [summary.recentParticipants]);

    const handlePress = useCallback(() => onOpen(summary), [onOpen, summary]);
    const handleOptionsPress = useCallback(() => {
      onShowOptions?.(summary);
    }, [onShowOptions, summary]);

    const containerStyle = useMemo<ViewStyle[]>(() => {
      const base: ViewStyle[] = [
        styles.container,
        {
          backgroundColor: theme.colors.surface.secondary,
          borderColor: theme.colors.border.secondary,
        },
      ];
      return base;
    }, [theme.colors.surface.secondary, theme.colors.border.secondary]);

    const replyLabel = formatReplyCount(summary.replyCount);
    const leadPreview = lead?.displayName ?? '';

    return (
      <Pressable
        onPress={handlePress}
        accessibilityRole="button"
        accessibilityLabel={threadsStrings.ping_threads_view_thread}
        accessibilityHint={replyLabel}
        testID={testID}
        style={({ pressed }) => [...containerStyle, pressed && styles.pressed]}
      >
        <View style={hovered ? styles.headerRowHover : styles.headerRow}>
          <View style={styles.headerLeading}>
            <ThreadIcon size={ICON_SIZE} color={theme.colors.text.secondary} />
            <Text
              style={[styles.headerLabel, { color: theme.colors.text.secondary }]}
              numberOfLines={1}
            >
              {replyLabel}
            </Text>
          </View>
          {hovered && onShowOptions !== undefined && (
            <Pressable
              onPress={handleOptionsPress}
              accessibilityRole="button"
              accessibilityLabel={threadsStrings.ping_threads_panel_options}
              hitSlop={8}
              style={({ pressed }) => [pressed && styles.pressed]}
            >
              <View
                style={[styles.dotsIcon, { backgroundColor: 'transparent' }]}
                accessibilityElementsHidden
              >
                <Dot color={theme.colors.text.secondary} />
                <Dot color={theme.colors.text.secondary} />
                <Dot color={theme.colors.text.secondary} />
              </View>
            </Pressable>
          )}
        </View>
        <View style={styles.previewRow}>
          {lead !== null && (
            <View style={styles.leadAvatar}>
              <View style={styles.leadAvatarScale}>
                <Avatar
                  size="small"
                  {...(lead.imageUri !== null ? { imageUri: lead.imageUri } : {})}
                  initials={lead.displayName}
                />
              </View>
            </View>
          )}
          <View style={styles.previewBody}>
            {leadPreview.length > 0 && (
              <Text
                style={[styles.leadName, { color: theme.colors.text.primary }]}
                numberOfLines={1}
              >
                {leadPreview}
              </Text>
            )}
            <Text
              style={[styles.previewText, { color: theme.colors.text.primary }]}
              numberOfLines={1}
            >
              {summary.closedByAuthor
                ? threadsStrings.ping_threads_composer_disabled_hint
                : threadsStrings.ping_threads_panel_subtitle(summary.replyCount)}
            </Text>
          </View>
          {hovered && (
            <View style={styles.viewThreadCta}>
              <Text style={[styles.viewThreadText, { color: theme.colors.accent.indigo }]}>
                {threadsStrings.ping_threads_view_thread}
              </Text>
            </View>
          )}
        </View>
      </Pressable>
    );
  },
);

ThreadInChat.displayName = 'ThreadInChat';

const ThreadIcon: React.FC<{ readonly size: number; readonly color: string }> = ({
  size,
  color,
}) => (
  <View
    style={{
      width: size,
      height: size,
      borderRadius: size / 4,
      borderWidth: 1.5,
      borderColor: color,
    }}
    accessibilityElementsHidden
    importantForAccessibility="no"
  />
);

const Dot: React.FC<{ readonly color: string }> = ({ color }) => (
  <View
    style={{
      width: 3,
      height: 3,
      borderRadius: 1.5,
      backgroundColor: color,
      marginHorizontal: 1,
    }}
  />
);

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
    borderWidth: 1,
    width: 380,
    maxWidth: '100%',
  } satisfies ViewStyle,
  pressed: {
    opacity: 0.85,
  } satisfies ViewStyle,
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  } satisfies ViewStyle,
  headerRowHover: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  } satisfies ViewStyle,
  headerLeading: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  } satisfies ViewStyle,
  headerLabel: {
    fontSize: 12,
    fontWeight: '700',
    lineHeight: 16.8,
  } satisfies TextStyle,
  dotsIcon: {
    width: 16,
    height: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  } satisfies ViewStyle,
  previewRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  } satisfies ViewStyle,
  leadAvatar: {
    width: LEAD_AVATAR_SIZE,
    height: LEAD_AVATAR_SIZE,
    alignItems: 'center',
    justifyContent: 'center',
  } satisfies ViewStyle,
  // The Avatar `small` variant renders at 32px circular; scale it down to
  // the inline ~20px lead-avatar size so it stays circular and uncropped.
  leadAvatarScale: {
    transform: [{ scale: LEAD_AVATAR_SIZE / 32 }],
  } satisfies ViewStyle,
  previewBody: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    minWidth: 0,
  } satisfies ViewStyle,
  leadName: {
    fontSize: 12,
    fontWeight: '700',
    lineHeight: 16.8,
  } satisfies TextStyle,
  previewText: {
    flex: 1,
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 16.8,
  } satisfies TextStyle,
  viewThreadCta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  } satisfies ViewStyle,
  viewThreadText: {
    fontSize: 12,
    fontWeight: '700',
    lineHeight: 16.8,
  } satisfies TextStyle,
});
